import { supabase } from '@/integrations/supabase/client';

export interface BookingGeographicData {
  country: string;
  sessions: number;
  users: number;
  percentage: number;
  bookingCount: number;
  totalRevenue: number;
}

export const getBookingGeographicData = async (timeRange: string = 'month'): Promise<BookingGeographicData[]> => {
  try {
    // Calculate date range
    const now = new Date();
    const startDate = new Date();
    
    switch (timeRange) {
      case 'week':
        startDate.setDate(now.getDate() - 7);
        break;
      case 'quarter':
        startDate.setMonth(now.getMonth() - 3);
        break;
      case 'year':
        startDate.setFullYear(now.getFullYear() - 1);
        break;
      default: // month
        startDate.setMonth(now.getMonth() - 1);
    }

    // Query bookings with client location data
    const { data: bookings, error } = await supabase
      .from('bookings')
      .select(`
        id,
        price_at_booking,
        created_at,
        client_id,
        clients!inner (
          id,
          country
        )
      `)
      .gte('created_at', startDate.toISOString())
      .eq('payment_status', 'Paid');

    if (error) {
      console.error('Error fetching booking geographic data:', error);
      return [];
    }

    if (!bookings || bookings.length === 0) {
      return [];
    }

    // Group bookings by country
    const countryStats = new Map<string, {
      bookingCount: number;
      totalRevenue: number;
      uniqueClients: Set<string>;
    }>();

    bookings.forEach((booking: any) => {
      const country = booking.clients?.country || 'Unknown';
      const clientId = booking.client_id;
      const revenue = parseFloat(booking.price_at_booking) || 0;

      if (!countryStats.has(country)) {
        countryStats.set(country, {
          bookingCount: 0,
          totalRevenue: 0,
          uniqueClients: new Set()
        });
      }

      const stats = countryStats.get(country)!;
      stats.bookingCount++;
      stats.totalRevenue += revenue;
      stats.uniqueClients.add(clientId);
    });

    // Convert to array and calculate percentages
    const totalBookings = bookings.length;
    const result: BookingGeographicData[] = Array.from(countryStats.entries())
      .map(([country, stats]) => ({
        country,
        sessions: stats.bookingCount, // Using booking count as "sessions"
        users: stats.uniqueClients.size,
        percentage: Math.round((stats.bookingCount / totalBookings) * 100),
        bookingCount: stats.bookingCount,
        totalRevenue: stats.totalRevenue
      }))
      .sort((a, b) => b.bookingCount - a.bookingCount)
      .slice(0, 10); // Top 10 countries

    return result;
  } catch (error) {
    console.error('Error in getBookingGeographicData:', error);
    return [];
  }
};